<template lang="pug">
el-dialog.start-timer-dialog(
    :title="isMimic ? $t('Support session') : $t('Track time on {desk}', { desk: deskLabel })"
    :visible="dialog.visible"
    width="520px"
    :close-on-click-modal="false"
    @close="cancel"
)
    p.dialog-intro {{ isMimic ? $t('You are entering this account as a support user. Should the time be tracked?') : $t('The clock starts now. What it is about can be filled in while it runs.') }}

    el-radio-group.mode-cards(v-model="mode")
        .mode-card(v-if="canCreateTickets" :class="{ 'is-active': mode === 'new' }" @click="mode = 'new'")
            el-radio(label="new")
                span {{ isMimic ? $t('Start a new support ticket') : $t('Start a new ticket') }}
            p.mode-card__hint {{ isMimic ? $t('Tracking starts now. You decide what happens to it when you leave the account.') : $t('A ticket is opened for it. Naming it can wait.') }}
            .mode-card__fields(v-if="mode === 'new'" @click.stop)
                el-form(label-position="top" size="small" @submit.native.prevent)
                    //- Optional on purpose: the clock starts when the phone
                    //- rings, and what the call is about is often only clear
                    //- once it is over.
                    el-form-item(:label="$t('Subject')")
                        el-input(
                            ref="subjectInput"
                            v-model="subject"
                            :placeholder="$t('What is being worked on? Can be filled in later.')"
                        )
                    //- Who the time is for. Our own desk starts with NOBODY
                    //- selected on purpose: an unattached session is internal
                    //- and bills no one, which is the safe way to be wrong.
                    el-form-item(v-if="showCustomerSelect" :label="$t('Website')")
                        el-select(
                            v-model="cmsId"
                            style="width: 100%"
                            :clearable="!isMimic"
                            :filterable="searchesCustomers"
                            :remote="searchesCustomers"
                            :remote-method="searchCustomers"
                            :loading="loadingCustomers"
                            :placeholder="isMimic ? $t('Select website') : $t('No website')"
                        )
                            el-option(v-for="cms in cmsOptions" :key="cms.id" :label="customerLabel(cms)" :value="cms.id")

        .mode-card(
            :class="{ 'is-active': mode === 'continue' }"
            @click="selectContinue"
        )
            el-radio(label="continue")
                span {{ $t('Continue an open ticket') }}
            p.mode-card__hint {{ $t('Books the time onto a ticket that is already open.') }}
            .mode-card__fields(v-if="mode === 'continue'" @click.stop)
                el-form(label-position="top" size="small" @submit.native.prevent)
                    el-form-item(v-if="isMimic && cmsOptions.length > 1" :label="$t('Website')")
                        el-select(v-model="cmsId" style="width: 100%" @change="fetchOpenTickets")
                            el-option(v-for="cms in cmsOptions" :key="cms.id" :label="customerLabel(cms)" :value="cms.id")
                    el-form-item(:label="$t('Ticket')")
                        el-select(
                            v-model="ticketId"
                            v-loading="loadingTickets"
                            filterable
                            style="width: 100%"
                            :placeholder="openTickets.length ? $t('Select ticket') : $t('No open tickets')"
                            popper-class="ticket-option-dropdown"
                            :no-data-text="$t('No open tickets')"
                        )
                            el-option(
                                v-for="ticket in openTickets"
                                :key="ticket.id"
                                :label="ticket.number + ' · ' + ticketSubject(ticket.subject, $t('No subject'))"
                                :value="ticket.id"
                            )
                                ticket-option(:ticket="ticket")

        //- Small in-between work: a call, a quick look, a five-minute fix. It
        //- still becomes a ticket behind the scenes — that is what carries the
        //- customer and the billing — it just stays out of the list.
        //- Offered while entering an account too. It used to be hidden there,
        //- on the grounds that a support session IS the record of what was done
        //- in it — true of a real session, not of a two-minute look at a
        //- setting. Without it that look had to be either a queue ticket nobody
        //- will action or unbilled time, when "billable but not on the list" is
        //- the exact case this card exists for.
        .mode-card(v-if="canCreateTickets" :class="{ 'is-active': mode === 'quick' }" @click="mode = 'quick'")
            el-radio(label="quick")
                span {{ $t('Without a ticket') }}
            p.mode-card__hint {{ $t('Stays out of the ticket list and is billed like any other effort.') }}
            .mode-card__fields(v-if="mode === 'quick'" @click.stop)
                el-form(label-position="top" size="small" @submit.native.prevent)
                    el-form-item(:label="$t('What is it about?')")
                        el-input(
                            ref="quickSubjectInput"
                            v-model="subject"
                            :placeholder="$t('Appears on the billing breakdown.')"
                        )
                    el-form-item(v-if="showCustomerSelect" :label="$t('Website')")
                        el-select(
                            v-model="cmsId"
                            style="width: 100%"
                            clearable
                            :filterable="searchesCustomers"
                            :remote="searchesCustomers"
                            :remote-method="searchCustomers"
                            :loading="loadingCustomers"
                            :placeholder="$t('No website')"
                        )
                            el-option(v-for="cms in cmsOptions" :key="cms.id" :label="customerLabel(cms)" :value="cms.id")

        //- Only when entering an account: outside one, not tracking simply
        //- means not opening this dialog.
        .mode-card(v-if="isMimic" :class="{ 'is-active': mode === 'none' }" @click="mode = 'none'")
            el-radio(label="none")
                span {{ $t('No tracking') }}
            p.mode-card__hint {{ $t('Take a quick look without billing anything.') }}

    //- One clock per desk: the server refuses a second one, and it used to
    //- refuse it AFTER the account had been entered, so the error came up
    //- over a mimic that had already happened and the modal hung there.
    //- Said here instead, as plain information: starting this one stops the
    //- running one with its elapsed time (the same defaults the stop sheet
    //- starts from; note and billable stay editable on the ticket). No
    //- second dialog on top of this one.
    .running-notice(v-if="replacesRunning")
        i.el-icon-fa-stopwatch.fas
        .running-notice__text
            strong {{ $t('A clock is already running on {number}.', { number: runningStash.ticketNumber }) }}
            p  {{ isMimic ? $t('It is stopped when you enter the account and its time is booked as elapsed.') : $t('It is stopped when this one starts and its time is booked as elapsed.') }}

    template(slot="footer")
        el-button(size="small" @click="cancel") {{ $t('Cancel') }}
        el-button(size="small" type="primary" :loading="starting" :disabled="!isValid" @click="confirm")
            | {{ isMimic ? $t('Enter account') : $t('Start') }}
</template>

<i18n>
{
    "en": {
        "Track time on {desk}": "Track time on {desk}",
        "A clock is already running on {number}.": "A clock is already running on {number}."
    },
    "de": {
        "Support session": "Support-Sitzung",
        "Track time": "Zeit erfassen",
        "Track time on {desk}": "Zeit erfassen auf {desk}",
        "Platform": "Plattform",
        "Website": "Webauftritt",
        "You are entering this account as a support user. Should the time be tracked?": "Sie betreten diesen Account als Support-Benutzer. Soll die Zeit erfasst werden?",
        "The clock starts now. What it is about can be filled in while it runs.": "Die Uhr läuft ab sofort. Worum es geht, lässt sich während der Erfassung ergänzen.",
        "Start a new support ticket": "Neues Support-Ticket starten",
        "Start a new ticket": "Neues Ticket starten",
        "Tracking starts now. You decide what happens to it when you leave the account.": "Die Erfassung startet jetzt. Beim Verlassen des Accounts entscheiden Sie, was damit geschieht.",
        "A ticket is opened for it. Naming it can wait.": "Dafür wird ein Ticket geöffnet. Der Betreff kann warten.",
        "Continue an open ticket": "Offenes Ticket fortsetzen",
        "Without a ticket": "Ohne Ticket",
        "Stays out of the ticket list and is billed like any other effort.": "Bleibt aus der Ticketliste heraus und wird wie jeder andere Aufwand berechnet.",
        "What is it about?": "Worum geht es?",
        "Appears on the billing breakdown.": "Erscheint in der Abrechnungsübersicht.",
        "Books the time onto a ticket that is already open.": "Bucht die Zeit auf ein bereits offenes Ticket.",
        "No tracking": "Ohne Erfassung",
        "Take a quick look without billing anything.": "Kurz nachsehen, ohne etwas zu berechnen.",
        "Subject": "Betreff",
        "What is being worked on? Can be filled in later.": "Woran wird gearbeitet? Kann später ergänzt werden.",
        "No subject": "Ohne Betreff",
        "Select website": "Webauftritt auswählen",
        "No website": "Kein Webauftritt",
        "Ticket": "Ticket",
        "Select ticket": "Ticket auswählen",
        "No open tickets": "Keine offenen Tickets",
        "Cancel": "Abbrechen",
        "Enter account": "Account betreten",
        "Start": "Starten",
        "An error occurred!": "Ein Fehler ist aufgetreten!",
        "Time tracking could not be started.": "Die Zeiterfassung konnte nicht gestartet werden.",
        "A clock is already running on {number}.": "Auf {number} läuft bereits eine Erfassung.",
        "It is stopped when you enter the account and its time is booked as elapsed.": "Sie wird beim Betreten des Accounts beendet und mit der verstrichenen Zeit gebucht.",
        "It is stopped when this one starts and its time is booked as elapsed.": "Sie wird beim Start beendet und mit der verstrichenen Zeit gebucht.",
        "The running clock could not be stopped.": "Die laufende Erfassung konnte nicht beendet werden.",
        "You are in the account, without tracking.": "Sie sind im Account, ohne Erfassung."
    }
}
</i18n>

<script>
/**
 * Starting a timer, from either of the two places it can start: entering a
 * customer's account, and the header pill. The only difference is what happens
 * afterwards — a mimic, or nothing — so it is one dialog with one extra card
 * rather than two that drift apart.
 *
 * The entry always starts on the token the DESK needs: ours on the primary
 * token (a mimic starts it before the mimic token exists), a shop's own on the
 * ambient one.
 *
 * Each choice is a card that carries its own fields, so it is always obvious
 * which inputs belong to the decision being made.
 */
import _ from 'lodash';
import { useAuthStore, useSetupStore, useTicketStore } from '@/stores';
import { checkRestrictions } from '@/helpers';

import { ticketSubject } from '@/filters';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import { customerLabel } from '@/extensions/tickets/customerLabel';

import TicketOption from '@/extensions/tickets/components/TicketOption';
import { TICKET_OPEN_STATUS_KEYS } from '@/extensions/tickets/ticketStatus';


export default {
    name: 'StartTimerDialog',
    components: {
        'ticket-option': TicketOption,
    },
    data() {
        return {
            mode: 'new',
            subject: '',
            cmsId: null,
            cmsOptions: [],
            loadingCustomers: false,
            ticketId: null,
            openTickets: [],
            loadingTickets: false,
            starting: false,
        };
    },
    computed: {
        ticketStore() {
            return useTicketStore();
        },
        // Super users pass, so our desk always offers every mode.
        canCreateTickets() {
            return checkRestrictions(['ex:tckt:tickets'], 'create');
        },
        dialog() {
            return this.ticketStore.startDialog;
        },
        // Entering an account, as opposed to just starting a clock.
        isMimic() {
            return !!this.dialog.userId;
        },
        desk() {
            return this.dialog.desk || 'platform';
        },
        isPlatform() {
            return this.desk === 'platform';
        },
        // Which desk this start is for. Two pills can offer this dialog, and an
        // unlabelled one says nothing about which clock is about to run.
        deskLabel() {
            if (this.isPlatform) return this.$t('Platform');

            return _.get(useSetupStore(), 'activeCms.name') || this.$t('Website');
        },
        // Our own session runs on OUR token even from inside a customer's
        // account; a shop's own timer runs on the account's own.
        // Our own desk supports ANY customer, not only the shops this user
        // happens to administer, so it searches instead of listing. Entering an
        // account picks between that account's own shops; a shop's own desk has
        // nothing to choose.
        searchesCustomers() {
            return this.isPlatform && !this.isMimic;
        },
        showCustomerSelect() {
            return this.searchesCustomers || (this.isMimic && this.cmsOptions.length > 1);
        },
        isValid() {
            // A nameless start is the point: nothing has to be typed.
            if (this.mode === 'continue') return !!this.ticketId;

            return true;
        },
        // The clock already running on the desk this dialog would start one on.
        runningStash() {
            return this.isPlatform ? this.ticketStore.platformTrackingStash : this.ticketStore.trackingStash;
        },
        replacesRunning() {
            return !!this.runningStash && this.mode !== 'none';
        },
    },
    watch: {
        'dialog.visible'(visible) {
            if (!visible) return;

            // The stash is what the tab wrote down; the server is what is
            // actually running. A clock stopped elsewhere would otherwise be
            // announced as about to be replaced, and then refused.
            this.ticketStore.syncRunningSession(
                this.desk,
                this.isPlatform ? null : this.dialog.cmsId || _.get(useSetupStore(), 'activeCms.id')
            );

            // A new ticket and a quick task both open a ticket, the create
            // flag; without it the only start is on one that already exists.
            this.mode = this.canCreateTickets ? 'new' : 'continue';
            this.subject = '';
            this.ticketId = null;
            this.openTickets = [];
            this.cmsOptions = this.dialog.cmsOptions || [];
            // Entering an account tracks against that account. Our own desk
            // starts unattached, so a session nobody picked a customer for
            // stays internal instead of landing on whichever shop happens to
            // be first in the list.
            this.cmsId =
                this.dialog.cmsId ||
                (this.searchesCustomers || !this.cmsOptions.length ? null : this.cmsOptions[0].id);

            this.$nextTick(() => {
                if (this.mode === 'continue') this.fetchOpenTickets();
                const input = this.$refs.subjectInput;
                if (input) input.focus();
            });
        },
    },
    methods: {
        customerLabel,
        ticketSubject,
        searchCustomers(term) {
            if (!term) return;

            this.loadingCustomers = true;
            // Our own token: inside an account the ambient one would answer
            // with that account's shops, which is not who we support.
            TicketApi.searchCms(term)
                .then((items) => {
                    this.cmsOptions = items;
                })
                .finally(() => {
                    this.loadingCustomers = false;
                });
        },
        selectContinue() {
            this.mode = 'continue';
            if (!this.openTickets.length) this.fetchOpenTickets();
        },
        fetchOpenTickets() {
            this.loadingTickets = true;
            this.ticketId = null;

            const filters = [{ key: 'status', in: TICKET_OPEN_STATUS_KEYS }];
            if (this.cmsId) filters.push({ key: 'cms', match: this.cmsId });

            TicketApi.fetchPaginated({
                currentPage: 1,
                perPage: 50,
                order: 'created_at',
                direction: 'desc',
                link: '',
                desk: this.desk,
                filters,
            })
                .then((data) => {
                    this.openTickets = data.items;
                })
                .finally(() => {
                    this.loadingTickets = false;
                });
        },
        cancel() {
            this.ticketStore.closeStartDialog();
        },
        async confirm() {
            // Captured BEFORE closeStartDialog(): `isMimic` is computed off
            // `dialog.userId`, and closing the dialog resets that to null.
            const userId = this.dialog.userId;
            const isMimic = this.isMimic;
            const desk = this.desk;
            const cmsId = this.cmsId;

            if (this.mode === 'none') {
                // Carry the website through. Mimicking without tracking used to
                // drop it, so a mimic started from ONE website's row landed on
                // whichever website the owner happens to administer first.
                this.ticketStore.closeStartDialog();
                useAuthStore().mimic(userId, { name: 'dashboard' }, cmsId);
                return;
            }

            let entered = false;

            this.starting = true;
            try {
                // The running clock goes first, BEFORE the account is entered:
                // if stopping it fails nothing else has happened yet, and the
                // dialog is still the place to say so. Elapsed time, billable,
                // no note - the stop sheet's own starting values; both stay
                // editable on the ticket.
                if (this.replacesRunning) {
                    try {
                        await this.ticketStore.stopRunningClock(desk);
                    } catch (error) {
                        /* eslint no-console: ["error", { allow: ["error"] }] */
                        console.error(error);
                        this.$notify.error({
                            title: this.$t('The running clock could not be stopped.'),
                            message: _.get(error, 'message') || '',
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });

                        return;
                    }
                }

                // Enter FIRST, then start the clock from inside the account.
                // The server then knows this is a support session - the entry
                // is attributed to us, not to the customer, and labelled as a
                // session by fact rather than by a flag we send. If we cannot
                // get in, no clock starts.
                if (isMimic && ! await useAuthStore().mimic(userId, { name: 'dashboard' }, cmsId)) {
                    return;
                }
                entered = isMimic;

                // No "is one already running" question here: the server answers
                // it per DESK, and its 422 names the ticket that blocks. Asking
                // from the client could only ever ask about one desk, and asked
                // about the wrong one.
                const entry = await TicketApi.startSupportTimeEntry(
                    this.mode === 'continue'
                        ? { ticketId: this.ticketId }
                        : {
                              newTicket: {
                                  subject: this.subject.trim(),
                                  desk,
                                  quick_task: this.mode === 'quick',
                              },
                              cmsId,
                          }
                );

                this.ticketStore.writeTrackingStash(desk, {
                    entryId: entry.id,
                    ticketId: _.get(entry, 'ticket.id'),
                    ticketNumber: _.get(entry, 'ticket.number'),
                    subject: _.get(entry, 'ticket.subject'),
                    conversationId: _.get(entry, 'ticket.conversation_id'),
                    startedAt: entry.started_at,
                    pausedAt: entry.paused_at || null,
                    pausedSeconds: entry.paused_seconds || 0,
                    desk,
                    cmsId: _.get(entry, 'ticket.cms.id') || null,
                    cmsName: _.get(entry, 'ticket.cms.name') || null,
                    // A support session, by the server's own account (started
                    // from inside the customer's account): leaving that account
                    // is the moment to ask about it. A timer that merely
                    // happens to be running while somebody mimics is not.
                    mimic: entry.source === 'mimic',
                });

                this.ticketStore.closeStartDialog();
                this.ticketStore.sessionChanged();
            } catch (error) {
                /* eslint no-console: ["error", { allow: ["error"] }] */
                console.error(error);
                // The account HAS been entered by now (another tab started a
                // clock in between, say): a dialog left open over it asks a
                // question that no longer has an answer, so it goes, and the
                // message says where you are.
                if (entered) this.ticketStore.closeStartDialog();
                this.$notify.error({
                    title: entered ? this.$t('You are in the account, without tracking.') : this.$t('An error occurred!'),
                    // The server says WHICH session is still running; a generic
                    // message leaves the only way out of the block to guesswork.
                    message: _.get(error, 'message') || this.$t('Time tracking could not be started.'),
                    iconClass: 'fas el-icon-fa-xmark',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 8000,
                });
            } finally {
                this.starting = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.dialog-intro
    margin: 0 0 1rem
    line-height: 1.45
    color: #606266

.mode-cards
    display: block

.mode-card
    display: block
    padding: 12px 14px
    border: 1px solid #dcdfe6
    border-radius: 6px
    cursor: pointer

    & + .mode-card
        margin-top: 8px

    &.is-active
        border-color: #409EFF
        background: #f4f9ff

    &.is-disabled
        opacity: .6
        cursor: not-allowed

    &__hint
        margin: 4px 0 0 24px
        font-size: .78rem
        line-height: 1.4
        color: #909399

    &__fields
        margin: 10px 0 0 24px
        cursor: default

.running-notice
    display: flex
    align-items: center
    gap: 10px
    margin-top: 12px
    padding: 10px 12px
    border: 1px solid #faecd8
    border-radius: 6px
    background: #fdf6ec
    color: #606266
    font-size: .82rem
    line-height: 1.4

    i
        color: #E6A23C

    &__text
        flex: 1

    strong
        color: #303133
</style>
